import { endpoints } from '../api/client.js';
import { MfaKind } from '../api/types.js';
import type { FlowContext } from '../state/flow-context.js';

/**
 * The distinct second-factor kinds the half-authenticated user can actually sign in with.
 *
 * Prefers the methods the password step returned (cached on the shell's flow context) and falls
 * back to GET /api/mfa/methods (e.g. after a reload, when the in-memory flow context is empty).
 * Disabled methods are excluded — they cannot satisfy the second step.
 *
 * Shared by the choose-method, passkey-login and code-entry pages so every screen agrees on which
 * fallbacks to offer (never offer "use an authenticator code" to a passkey-only user).
 */
export async function resolveMfaKinds(flow: FlowContext | undefined): Promise<MfaKind[]> {
  const cached = flow?.methods ?? [];
  if (cached.length) return distinct(cached.map((m) => m.kind));

  const methods = await endpoints.mfaMethods();
  return distinct(
    methods
      .filter((m) => !m.disabled)
      .map((m) => (m.kind === 'passkey' ? MfaKind.Passkey : MfaKind.Totp)),
  );
}

function distinct(kinds: MfaKind[]): MfaKind[] {
  return [...new Set(kinds)];
}
